import { useState, useRef, useCallback } from 'react';
import { FlaskConical, X, GripHorizontal, CheckSquare, Square, Send } from 'lucide-react';
import { scPost } from '@/lib/scFetch';

const ITEMS = [
  'The optimizer UI loaded without errors',
  'Resource folders list populated correctly',
  'Ran an optimization job on at least 1 resource',
  'Job completed without crashing (success or fail is fine)',
  'Backup files were created in the backup directory',
] as const;

interface Props {
  testerName: string;
  onClose: () => void;
}

export default function TestingChecklist({ testerName, onClose }: Props) {
  const [checked, setChecked] = useState<boolean[]>(ITEMS.map(() => false));
  const [bugReport, setBugReport] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const panelRef = useRef<HTMLDivElement>(null);
  const dragOffset = useRef({ x: 0, y: 0 });
  const pos = useRef({ x: window.innerWidth / 2 - 180, y: 80 });
  const [, forceRender] = useState(0);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button,textarea,input,label')) return;
    e.preventDefault();
    dragOffset.current = {
      x: e.clientX - pos.current.x,
      y: e.clientY - pos.current.y,
    };
    const onMove = (ev: MouseEvent) => {
      pos.current = {
        x: Math.max(0, Math.min(window.innerWidth - 360, ev.clientX - dragOffset.current.x)),
        y: Math.max(0, Math.min(window.innerHeight - 100, ev.clientY - dragOffset.current.y)),
      };
      if (panelRef.current) {
        panelRef.current.style.left = pos.current.x + 'px';
        panelRef.current.style.top = pos.current.y + 'px';
      }
      forceRender(n => n + 1);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, []);

  const toggle = (i: number) => setChecked(prev => prev.map((v, idx) => idx === i ? !v : v));

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await scPost('/fivem/toolkitv/testing-submit', {
        testerName,
        results: ITEMS.map((label, i) => ({ label, checked: checked[i] })),
        bugReport: bugReport.trim(),
      });
      setSubmitted(true);
    } catch (err) {
      console.error('Failed to submit test results', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      ref={panelRef}
      style={{ left: pos.current.x, top: pos.current.y, zIndex: 9999, width: 360 }}
      className="fixed rounded-2xl border border-white/[0.08] shadow-2xl overflow-hidden"
      onMouseDown={onMouseDown}
    >
      {/* drag handle / header */}
      <div
        className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06] cursor-grab active:cursor-grabbing select-none"
        style={{ background: 'rgba(0,255,209,0.06)' }}
      >
        <div className="flex items-center gap-2 text-[11px] font-black text-[#00ffd1] uppercase tracking-widest">
          <FlaskConical size={13} />
          Tester Checklist
        </div>
        <div className="flex items-center gap-2">
          <GripHorizontal size={14} className="text-slate-600" />
          <button onClick={onClose} className="text-slate-500 hover:text-slate-300 transition-colors">
            <X size={14} />
          </button>
        </div>
      </div>

      <div className="p-4 flex flex-col gap-3" style={{ background: '#0d1018' }}>
        {submitted ? (
          <div className="py-4 text-center">
            <div className="text-[#00ffd1] font-black text-sm mb-1">✅ Report Sent</div>
            <p className="text-[10px] text-slate-400">Your test results have been sent to AISc0pez. Thanks for testing!</p>
            <button onClick={onClose} className="mt-3 px-4 py-1.5 rounded-lg bg-white/[0.04] border border-white/[0.08] text-[10px] text-slate-300 font-bold hover:bg-white/[0.08] transition-all">
              Close
            </button>
          </div>
        ) : (
          <>
            <p className="text-[9.5px] text-slate-400 leading-relaxed">
              Tick each item as you test. Submit when done — results go straight to the owner.
            </p>

            <div className="flex flex-col gap-2">
              {ITEMS.map((label, i) => (
                <label key={i} className="flex items-start gap-2.5 cursor-pointer select-none group">
                  <button
                    onClick={() => toggle(i)}
                    className="mt-0.5 text-slate-500 group-hover:text-slate-300 transition-colors shrink-0"
                  >
                    {checked[i]
                      ? <CheckSquare size={14} className="text-[#00ffd1]" />
                      : <Square size={14} />
                    }
                  </button>
                  <span className={`text-[11px] leading-snug transition-colors ${checked[i] ? 'text-[#00ffd1] line-through opacity-70' : 'text-slate-200'}`}>
                    {label}
                  </span>
                </label>
              ))}
            </div>

            <div>
              <label className="text-[9px] font-bold uppercase text-slate-500 block mb-1">Bug report (optional)</label>
              <textarea
                value={bugReport}
                onChange={e => setBugReport(e.target.value)}
                rows={3}
                placeholder="Describe any bugs you found..."
                className="w-full bg-black/40 border border-white/[0.06] rounded-lg px-3 py-2 text-[11px] text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-[#00ffd1]/40 resize-none"
              />
            </div>

            <button
              onClick={handleSubmit}
              disabled={submitting || checked.every(v => !v)}
              className="flex items-center justify-center gap-2 w-full py-2 rounded-xl font-black text-[10px] text-slate-950 hover:opacity-90 disabled:opacity-40 transition-all uppercase"
              style={{ background: 'linear-gradient(90deg,#00ffd1,#a78bfa)' }}
            >
              <Send size={11} />
              {submitting ? 'Sending...' : 'Submit Test Report'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
